import React, {Fragment, useEffect, useState} from "react";
import {
    Paper,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Table,
    Grid2 as Grid,
    Button,
    Box,
    Typography
} from "@mui/material";
import {makeStyles} from "@mui/styles"
import {Trans, useTranslation} from "react-i18next";
import {useAtomValue} from "jotai";
import {
    VIRTUOSO_MIN_LENGTH,
} from "../constants";
import {
    pushAnalytics,
    rowSx,
} from "../helpers";
import CreateWorkLogDialog from "./CreateWorkLogDialog";
import DeleteWorkLogDialog from "./DeleteWorkLogDialog";
import UpdateWorkLogDialog from "./UpdateWorkLogDialog";
import DetailsDialog from "./DetailsDialog";
import ChartsDialog from "./ChartsDialog";
import {TableVirtuoso} from 'react-virtuoso';
import InsightsDialog from "./InsightsDialog";
import {
    resultRowsAtom,
    myUserAtom,
    datesAtom,
    workLogsAtom
} from "../jotai/atoms";
import {ExportButton} from "./ExportButton";
import {ResultTableRowContent} from "./ResultTable/RowContent";
import {useDateFormatter} from "../hooks";
import {DialogsRestrictions} from "./Dialogs/Restrictions";
import {ResultTableHeader} from "./ResultTable/Header";
import {extractRawMinutesFromDuration} from "../helpers/duration";

const useStyles = makeStyles({
    table: {
        minWidth: 650
    }
});

const VirtuosoTableComponents = {
    Scroller: React.forwardRef((props, ref) => (
        <TableContainer component={Paper} {...props} ref={ref}/>
    )),
    Table: (props) => (
        <Table {...props} sx={{borderCollapse: 'separate', tableLayout: 'fixed'}}/>
    ),
    TableHead,
    TableRow: ({item: _item, ...props}) => {
        const {context: {rows,}, ...otherProps} = props;
        const index = props['data-index'];
        const item = rows[index];

        return <TableRow {...otherProps} sx={rowSx(item, item.realIndex)}/>
    },
    TableBody: React.forwardRef((props, ref) => (
        <TableBody {...props} ref={ref}/>
    )),
};

function ResultTable() {
    const {t} = useTranslation();
    const classes = useStyles();

    const rows = useAtomValue(resultRowsAtom);
    const myUser = useAtomValue(myUserAtom);
    const dates = useAtomValue(datesAtom);
    const workLogs = useAtomValue(workLogsAtom);

    const { formatDate } = useDateFormatter();

    const [restrictionsDialog, setRestrictionsDialog] = useState(false);
    const [chartsDialog, setChartsDialog] = useState(false);

    const [useVirtuoso, setUseVirtuoso] = useState(false);

    useEffect(() => {
        setUseVirtuoso(rows.length > VIRTUOSO_MIN_LENGTH);
    }, [rows]);

    const showCharts = () => {
        setChartsDialog(true);
    };

    // Расчет дашборда: 40 часов на неделю и сколько уже списано
    const calculateDashboard = () => {
        const targetHoursPerWeek = 40;
        const targetMinutesPerWeek = targetHoursPerWeek * 60;
        
        // Подсчитываем общее время из workLogs
        let totalMinutes = 0;
        for (const workLog of workLogs) {
            totalMinutes += extractRawMinutesFromDuration(workLog.duration);
        }
        
        const totalHours = Math.floor(totalMinutes / 60);
        const remainingMinutes = totalMinutes % 60;
        const remainingHours = targetHoursPerWeek - totalHours;
        const remainingTotalMinutes = targetMinutesPerWeek - totalMinutes;
        
        return {
            targetHours: targetHoursPerWeek,
            totalHours,
            totalMinutes,
            remainingHours,
            remainingTotalMinutes,
            progress: Math.min(100, (totalMinutes / targetMinutesPerWeek) * 100)
        };
    };

    const dashboard = calculateDashboard();

    return <Fragment>
        <InsightsDialog />

        <ChartsDialog state={chartsDialog} handleClose={() => setChartsDialog(false)}/>

        <DialogsRestrictions state={restrictionsDialog} handleClose={() => setRestrictionsDialog(false)}/>
        <CreateWorkLogDialog />
        <DeleteWorkLogDialog />
        <UpdateWorkLogDialog />

        <DetailsDialog/>

        <Grid container spacing={2}>
            {/* Дашборд */}
            <Grid size={12}>
                <Box sx={{ 
                    p: 2, 
                    mb: 2, 
                    borderRadius: 1, 
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: 'divider'
                }}>
                    {dashboard.totalMinutes >= dashboard.targetHours * 60 ? (
                        <Box sx={{
                            p: 2,
                            borderRadius: 1,
                            bgcolor: 'success.light',
                            color: 'success.contrastText',
                            textAlign: 'center'
                        }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {t('components:result_table.dashboard.all_hours_logged')}
                            </Typography>
                            <Typography variant="body2">
                                {t('components:result_table.dashboard.all_hours_subtitle', {
                                    total: dashboard.totalHours,
                                    target: dashboard.targetHours
                                })}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography variant="h6" gutterBottom>
                            {t('components:result_table.dashboard.title', { 
                                target: dashboard.targetHours,
                                total: dashboard.totalHours,
                                remaining: Math.floor(dashboard.remainingTotalMinutes / 60)
                            })}
                        </Typography>
                    )}
                </Box>
            </Grid>

            <Grid size={{xs: 12, md: 2}}>
                <Button fullWidth color="info" variant="outlined" onClick={() => {
                    showCharts();
                    pushAnalytics("chartsButtonClick");
                }}>{t('common:button.charts')}</Button>
            </Grid>

            <Grid size={{xs: 12, md: 2}}>
                <ExportButton/>
            </Grid>

            <Grid size={12}>
                {useVirtuoso && <Paper style={{height: 670, width: '100%'}}>
                    <TableVirtuoso
                        context={{rows}}
                        data={rows}
                        itemContent={(index, row) => <ResultTableRowContent row={row} index={index} />}
                        fixedHeaderContent={() => <ResultTableHeader />}
                        components={VirtuosoTableComponents}/>
                </Paper>}
                {!useVirtuoso && <TableContainer component={Paper} sx={{maxHeight: 670}}>
                    <Table stickyHeader className={classes.table} style={{tableLayout: "fixed"}}>
                        <TableHead>
                            <ResultTableHeader />
                        </TableHead>
                        <TableBody>
                            {rows.map((row, index) => <TableRow sx={rowSx(row, row.realIndex)}
                                                                key={`table-row-${index}`}>
                                <ResultTableRowContent row={row} index={index} />
                            </TableRow>)}
                        </TableBody>
                    </Table>
                </TableContainer>}
            </Grid>
        </Grid>
    </Fragment>
}

export default ResultTable;